import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Descriptions, Tag, Table, Collapse } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import ShelfContext from "../../../../context/wms/shelf/context";
import ShelfContextProvider from "../../../../context/wms/shelf/provider";
import Spin from "../../../general/antd/spin";
import PageDetails from "../../../general/details";
import { BinPosition } from "../../../../models/wms/enums";

const Details = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { details, actions, loading } = useContext(ShelfContext);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  if (loading.includes("details")) {
    return <Spin />;
  }

  const getBinPositionLabel = (pos?: BinPosition) => {
    if (pos === BinPosition.LEFT) return t("position_left");
    if (pos === BinPosition.CENTER) return t("position_center");
    if (pos === BinPosition.RIGHT) return t("position_right");
    return "-";
  };

  return (
    <PageDetails
      title={t("details")}
      subtitle={t("shelf")}
      buttonName={`${t("update_shelf")}`}
      onClick={() => window.location.assign(`/wms/shelves/update/${id}`)}
      loading={loading.includes("details")}
      onRefresh={() => actions.getDetails(Number(id))}
    >
      <Descriptions
        bordered
        column={1}
        size="middle"
      >
        <Descriptions.Item label={t("id")}>{details?.id}</Descriptions.Item>
        {details?.translations?.name?.map((translation) => (
          <Descriptions.Item
            key={translation.lang}
            label={`${t("name")} (${translation.lang})`}
          >
            {translation.value || "-"}
          </Descriptions.Item>
        ))}
        <Descriptions.Item label={t("store")}>
          {details?.store?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("store_id")}>
          {details?.storeId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone")}>
          {details?.zone?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone_id")}>
          {details?.zoneId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle")}>
          {details?.aisle?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle_id")}>
          {details?.aisleId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("storage_unit")}>
          {details?.storageUnit?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("storage_unit_id")}>
          {details?.storageUnitId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("scanable_code")}>
          {details?.scanableCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("has_bins")}>
          {details?.hasBins ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t("yes")}
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">
              {t("no")}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("pick_sequence")}>
          {details?.pickSequence}
        </Descriptions.Item>
        <Descriptions.Item label={t("is_active")}>
          {details?.isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t("active")}
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">
              {t("inactive")}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_at")}>
          {details?.createdAt ? String(details.createdAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_by")}>
          {details?.createdBy
            ? `${details.createdBy.firstName} ${details.createdBy.lastName} (${details.createdBy.mobile})`
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_at")}>
          {details?.updatedAt ? String(details.updatedAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_by")}>
          {details?.updatedBy
            ? `${details.updatedBy.firstName} ${details.updatedBy.lastName} (${details.updatedBy.mobile})`
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      {details?.hasBins && details?.bins && details.bins.length > 0 && (
        <Collapse style={{ marginTop: 16 }} items={[
          {
            key: "bins",
            label: t("bins"),
            children: (
              <Table
                dataSource={details.bins}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: t("id"),
                    dataIndex: "id",
                    key: "id",
                    width: 80,
                  },
                  {
                    title: t("name"),
                    dataIndex: "name",
                    key: "name",
                  },
                  {
                    title: t("bin_position"),
                    dataIndex: "binPosition",
                    key: "binPosition",
                    render: (pos: BinPosition) => getBinPositionLabel(pos),
                  },
                  {
                    title: t("is_active"),
                    dataIndex: "isActive",
                    key: "isActive",
                    width: 100,
                    render: (isActive: boolean) => (
                      <Tag icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isActive ? "success" : "default"}>
                        {isActive ? t("active") : t("inactive")}
                      </Tag>
                    ),
                  },
                ]}
              />
            ),
          },
        ]} />
      )}
    </PageDetails>
  );
};

const ShelfDetails = () => {
  return (
    <ShelfContextProvider>
      <Details />
    </ShelfContextProvider>
  );
};

export default ShelfDetails;
